<template>
  <div class="stats-widget" :class="`stats-widget--${mode}`">
    <div class="stats-widget__header">
      <div
        class="stats-widget__type-switcher"
        :class="{ toggled: showDataTypesList }"
      >
        <div
          class="stats-widget__type-switcher__selected"
          @click="toggleSwitcher"
        >
          <div class="stats-widget__type-switcher__selected-title">
            {{ $t(selectedDataType.name) }}
          </div>
          <div class="stats-widget__type-switcher__selected-caret">
            <svg
              width="8"
              height="6"
              viewBox="0 0 8 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.31962 5.04484L7.84229 1.5221C7.92383 1.44063 7.96875 1.33186 7.96875 1.21589C7.96875 1.09992 7.92383 0.991154 7.84229 0.909677L7.58293 0.650252C7.41392 0.481443 7.13925 0.481443 6.9705 0.650252L4.01238 3.60838L1.05097 0.64697C0.969428 0.565494 0.860728 0.520508 0.74482 0.520508C0.628783 0.520508 0.520083 0.565494 0.438478 0.64697L0.179182 0.906395C0.0976415 0.987936 0.0527201 1.09664 0.0527201 1.21261C0.0527201 1.32858 0.0976415 1.43734 0.179182 1.51882L3.70507 5.04484C3.78687 5.12651 3.89608 5.17136 4.01218 5.17111C4.12873 5.17136 4.23788 5.12651 4.31962 5.04484Z"
                fill="#666D79"
              />
            </svg>
          </div>
        </div>
        <div class="stats-widget__type-switcher__list" v-if="showDataTypesList">
          <div
            class="stats-widget__type-switcher__list-item"
            v-for="_dataType in availableDataTypes"
            :key="`data-type-${_dataType.id}`"
            @click="setActiveDataType(_dataType)"
          >
            {{ $t(_dataType.name) }}
          </div>
        </div>
      </div>
      <div class="stats-widget__legend" v-if="this.isChartInitialized">
        <div
          class="stats-widget__legend-item"
          :class="{
            [`stats-widget__legend-item--${_label.text}`]: true,
            hidden: hiddenDatasets.includes(_label.text),
          }"
          v-for="_label in chart.legend.legendItems"
          :key="_label.text"
          @click="toggleChartData(_label)"
        >
          {{ $t(`stats-widget.datasets.${_label.text}.title`) }}
        </div>
      </div>
    </div>
    <div class="stats-widget__chart">
      <canvas ref="chart"></canvas>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { addTrailingZeros } from '@js-app/services/helpers/math'

const AVAILABLE_DATA_TYPES = Object.freeze([
  { id: 'count', name: 'stats-widget.data-types.count' },
  { id: 'sum', name: 'stats-widget.data-types.sum' },
])

const DATASETS_BASE = [
  {
    data: [],
    backgroundColor: '#75B04D',
    barThickness: 15,
    id: 'success',
    borderRadius: 20,
    borderSkipped: 'left',
    hoverBackgroundColor: '#75B04D',
    label: 'success',
    skipNull: true,
  },
  {
    data: [],
    backgroundColor: '#FE6C64',
    id: 'error',
    borderRadius: 20,
    borderSkipped: 'left',
    hoverBackgroundColor: '#FE6C64',
    barThickness: 15,
    label: 'error',
    skipNull: true,
  },
]

export default {
  name: 'StatsWidget',

  props: {
    stats: [Object, Array],
    mode: {
      type: String,
      default: 'base',
    },
  },

  data() {
    this.chart = null // НЕ реактивное поле
    return {
      isChartInitialized: false,
      selectedDataTypeId: AVAILABLE_DATA_TYPES[0].id,
      showDataTypesList: false,
      hiddenDatasets: [],
    }
  },

  created() {
    this.chartLabels = [] // НЕ реактивное поле
  },

  computed: {
    selectedDataType() {
      return AVAILABLE_DATA_TYPES.find(
          (dt) => dt.id === this.selectedDataTypeId
      )
    },
    availableDataTypes() {
      return AVAILABLE_DATA_TYPES.filter(
          (dt) => dt.id !== this.selectedDataTypeId
      )
    },
  },

  watch: {
    stats: {
      deep: true,
      handler: 'setChartData',
    },
    selectedDataTypeId: 'setChartData',
  },

  mounted() {
    try {
      this.chart = new Chart(this.$refs.chart, {
        plugins: [ChartDataLabels],
        type: 'bar',
        data: {
          labels: [],
          datasets: [...DATASETS_BASE],
        },
        options: {
          responsive: true,
          layout: {
            padding: {
              left: 20,
              right: 35,
              top: 0,
              bottom: 0,
            },
          },
          plugins: {
            legend: {
              display: false,
              labels: {
                boxWidth: 10,
                boxHeight: 10,
                font: {
                  color: '#67768A',
                  size: 12,
                  weight: 'medium',
                  family: 'Montserrat',
                },
              },
            },
            datalabels: {
              color: (context) => context.dataset.backgroundColor,
              display: (context) =>
                  context.dataset.data[context.dataIndex].tmp !== true,
              align: (context) =>
                  context.dataset.id === 'error' ? 'start' : 'end',
              anchor: (context) =>
                  context.dataset.id === 'error' ? 'start' : 'end',
              offset: () => 2,
              formatter: (value) => {
                value.value = value.value || 0

                const rounded = Math.abs(
                    Math.round((parseFloat(value.value) + Number.EPSILON) * 100) /
                    100
                )

                return rounded === 0 ? '' : `${rounded}%`
              },
              labels: {
                title: {
                  font: {
                    size: 10,
                    weight: 'bold',
                    family: 'Montserrat',
                  },
                },
              },
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              titleColor: '#ffffff',
              titleFont: {
                weight: 'bold',
                family: 'Montserrat',
                size: 12,
              },
              titleMarginBottom: 15,
              bodyColor: '#FFFFFF',
              bodyFont: {
                family: 'Montserrat',
                size: 12,
              },
              boxWidth: 11,
              boxHeight: 11,
              boxPadding: 5,
              mode: 'y',
              callbacks: {
                title: (context) =>
                    this.$t(`transaction-types.${context[0].label}`),
                label: (context) =>
                    `${this.$t(
                        `stats-widget.datasets.${context.dataset.id}.title`
                    )}: ${context.dataset.data[context.dataIndex].amount}`,
              },
            },
          },
          parsing: {
            xAxisKey: 'value',
          },
          indexAxis: 'y',
          scales: {
            x: {
              beginAtZero: true,
              grid: {
                color: '#F4F6F9',
                tickColor: '#D0D5DD',
                tickLength: 0,
              },
              offset: true,
              ticks: {
                padding: 10,
                font: {
                  family: 'Montserrat',
                  size: 12,
                },
                color: '#B9C4D8',
                callback: function (value) {
                  return Math.abs(value) % 10 === 0 ? Math.abs(value) : null
                },
              },
              min: -100,
              max: 100,
            },
            y: {
              grid: {
                color: '#F4F6F9',
                tickColor: '#D0D5DD',
                tickLength: 0,
              },
              type: 'category',
              bounds: 'data',
              ticks: {
                padding: 10,
                font: {
                  family: 'Montserrat',
                  size: 10,
                },
                color: '#B9C4D8',
                callback: (value, index) => {
                  if (this.mode === 'small') return ''
                  return this.chartLabels[index]
                      ? this.$t(`transaction-types.${this.chartLabels[index]}`)
                      : ''
                },
              },
              stacked: true,
            },
          },
        },
      })
      this.isChartInitialized = true;
      this.setChartData()
    } catch (e) {
      console.error('StatsWidget mounted hook error:', e)
    }
  },

  methods: {
    toggleChartData(label) {
      const idx = this.hiddenDatasets.indexOf(label.text)
      if (idx !== -1) {
        this.hiddenDatasets.splice(idx, 1)
      } else {
        this.hiddenDatasets.push(label.text)
      }

      this.chart.setDatasetVisibility(
          label.datasetIndex,
          !this.chart.isDatasetVisible(label.datasetIndex)
      )
      this.chart.update()
    },

    setChartData() {
      if (
          !this.chart ||
          !this.stats ||
          (Array.isArray(this.stats) && this.stats.length === 0)
      ) {
        return
      }

      this.chartLabels = Object.keys(this.stats).map((item) =>
          item === 'unknown' ? 'other' : item
      )

      let datasets = {}

      switch (this.selectedDataTypeId) {
        case 'count':
          datasets = this.getCountChartData()
          break
        case 'sum':
          datasets = this.getSumChartData()
          break
      }

      this.chart.data.labels = [...this.chartLabels]
      this.chart.data.datasets[0].data = datasets._successDataset
      this.chart.data.datasets[1].data = datasets._errorsDataset

      this.chart.update()
    },

    getCountChartData() {
      const success = []
      const errors = []

      for (const label of this.chartLabels) {
        const stat = this.stats[label] || {}
        const finishCount = parseInt(stat.Finish?.Count ?? 0, 10)
        const errorCount = parseInt(stat.Error?.Count ?? 0, 10)
        const total = finishCount + errorCount

        success.push({
          amount: finishCount,
          value: total > 0 ? (finishCount / total) * 100 : 0,
          y: label,
        })

        errors.push({
          amount: errorCount,
          value: total > 0 ? -(errorCount / total) * 100 : null,
          y: label,
        })
      }

      return { _successDataset: success, _errorsDataset: errors }
    },

    getSumChartData() {
      const success = []
      const errors = []

      for (const label of this.chartLabels) {
        const stat = this.stats[label] || {}
        const finishSum = parseInt(stat.Finish?.Sum ?? 0, 10)
        const errorSum = parseInt(stat.Error?.Sum ?? 0, 10)
        const total = finishSum + errorSum

        success.push({
          amount: addTrailingZeros((finishSum / 100).toLocaleString()),
          value: total > 0 ? (finishSum / total) * 100 : 0,
          y: label,
        })

        errors.push({
          amount: addTrailingZeros((errorSum / 100).toLocaleString()),
          value: total > 0 && errorSum > 0 ? -(errorSum / total) * 100 : null,
          y: label,
        })
      }

      return {
        _successDataset: success,
        _errorsDataset: errors,
      }
    },

    setActiveDataType(dataType) {
      this.showDataTypesList = false
      if (this.selectedDataTypeId === dataType.id) return
      this.selectedDataTypeId = dataType.id
    },

    toggleSwitcher() {
      this.showDataTypesList = !this.showDataTypesList
    },
  },
}

</script>



<style lang="scss" scoped>
.stats-widget {
  display: flex;
  flex-direction: column;
  &--small & {
    &__header {
      margin-bottom: 22px;
    }

    &__chart {
      padding-left: 5px;
      width: 100% !important;
      height: 213px !important;

      canvas {
      }
    }

    &__legend {
      margin-left: 45px;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    margin-bottom: 19px;
  }

  &__legend {
    margin-left: 95px;
    display: flex;
    align-items: center;

    &-item {
      display: flex;
      align-items: center;
      font-weight: 500;
      font-size: 12px;
      line-height: 12px;
      color: #67768a;
      margin-right: 18px;
      cursor: pointer;

      &:hover {
        opacity: 0.8;
      }

      &.hidden {
        text-decoration: line-through;
      }

      &:last-child {
        margin-right: 0;
      }

      &--success {
        &:before {
          background: #75b04d;
        }
      }

      &--error {
        &:before {
          background: #fe6c64;
        }
      }

      &:before {
        content: '';
        display: flex;
        width: 11px;
        height: 11px;

        border-radius: 2px;
        margin-right: 7px;
      }
    }
  }

  &__chart {
    padding-left: 25px;
    width: 555px !important;
    height: 246px !important;

    canvas {
    }
  }
  &__type-switcher {
    width: 103px;
    position: relative;

    &.toggled & {
      &__selected {
        &-caret {
          transform: translateY(-50%) rotate(-180deg);
        }
      }
    }

    &__selected {
      height: 47px;
      position: relative;
      display: flex;
      align-items: center;
      padding: 15px 10px;
      border: 1px solid #f4f3fc;
      box-shadow: 0px 4px 8px rgba(235, 241, 252, 0.38);
      border-radius: 8px;
      cursor: pointer;

      &-title {
        position: relative;
        font-weight: 700;
        font-size: 12px;
        line-height: 15px;
        color: #465a8b;
        z-index: 9;
      }

      &-caret {
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9;
      }
    }

    &__list {
      position: absolute;
      width: 100%;
      background: #fff;
      top: 0;
      left: 0;
      padding: 47px 15px 17px 10px;
      border: 1px solid #f4f3fc;
      box-shadow: 0px 4px 8px rgba(235, 241, 252, 0.38);
      border-radius: 8px;

      &-item {
        font-weight: 500;
        font-size: 12px;
        line-height: 15px;
        color: #465a8b;
        cursor: pointer;

        &:hover {
          opacity: 0.7;
        }
      }
    }
  }
}
</style>
